import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { keepPreviousData } from "@tanstack/react-query";

import type { ProWebsiteRankTrackerModelGscDeviceType } from "@similarweb/pro-api-client";

import { apiClient } from "app/modules/api-client/apiClient";
import {
    ALL_CAMPAIGNS_QUERY_PREFIX,
    RankTrackerQueryType,
} from "app/pages/digital-marketing/keywords-campaigns/shared/constants";
import { useRankTrackerQuery } from "app/pages/digital-marketing/keywords-campaigns/shared/hooks/useRankTrackerQuery";
import type { LocationDevice } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { createCampaignQueryKey } from "app/pages/digital-marketing/keywords-campaigns/shared/utils";

import { handleGscThrowOnError } from "../gscProblemErrors";
import type { GscKeyword } from "../types";

export type GscKeywordsQueryParams = {
    connectionId: number;
    site: string;
    countryCode: string | undefined;
    device: LocationDevice;
    startDate: string;
    endDate: string;
    limit?: number;
};

type QueryOptions = Omit<UseQueryOptions<GscKeyword[]>, "queryKey" | "queryFn">;

const DEFAULT_LIMIT = 5000;

const gscDeviceMap: Record<LocationDevice, ProWebsiteRankTrackerModelGscDeviceType> = {
    DESKTOP: "Desktop",
    MOBILE: "Mobile",
    BOTH: "Both",
};

const fetchGscKeywords = async (params: GscKeywordsQueryParams): Promise<GscKeyword[]> => {
    if (!params.countryCode) {
        return [];
    }
    return apiClient.RankTracker.rtGetGscKeywords({
        connectionId: params.connectionId,
        siteUrl: params.site,
        country: params.countryCode,
        device: gscDeviceMap[params.device],
        startDate: params.startDate,
        endDate: params.endDate,
        rowLimit: params.limit,
    });
};

/**
 * Fetches keywords (with clicks/impressions) for a GSC property, country, and device.
 * Disabled until countryCode is set; uses keepPreviousData for smooth transitions during re-fetches.
 */
export const useGscKeywordsQuery = (params: GscKeywordsQueryParams, options?: QueryOptions) => {
    const limit = params.limit ?? DEFAULT_LIMIT;

    const queryKey: QueryKey = createCampaignQueryKey(ALL_CAMPAIGNS_QUERY_PREFIX, RankTrackerQueryType.GscKeywords, [
        params.connectionId,
        params.site,
        params.countryCode,
        params.device,
        params.startDate,
        params.endDate,
        limit,
    ]);

    return useRankTrackerQuery({
        queryKey,
        queryFn: () => fetchGscKeywords({ ...params, limit }),
        enabled: !!params.countryCode,
        placeholderData: keepPreviousData,
        throwOnError: handleGscThrowOnError("Error fetching GSC keywords"),
        ...options,
    });
};
